
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import vue2Dropzone, { DropZoneOptions, DropZone } from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import API from '@/lib/API';
import {
  Lead, LeadType, User, Category, Tag, PermissionCode, AssignmentAssignee,
} from '@/types';
import UserGroups from '@/components/UserGroups.vue';
import InstallerAssigneesField from '@/components/InstallerAssigneesField.vue';

@Component({
  components: {
    UserGroups,
    InstallerAssigneesField,
    vueDropzone: vue2Dropzone,
  },
})
export default class LeadCreateChildDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;

  dialog = false
  valid = false
  loading = false

  newAttachments: number[] = []
  removedAttachments: number[] = []
  dz: DropZone | null = null

  dzOptions: DropZoneOptions = {
    url: '/upload/sopralluogoTecnico',
    addRemoveLinks: true,
    removeType: 'server',
    dictCancelUpload: 'Annulla caricamento',
    dictCancelUploadConfirmation: 'Confermi di voler annullare il caricamento?',
    dictRemoveFile: 'Rimuovi',
    dictDefaultMessage: 'Trascinare qui i file da caricare<br><small>oppure cliccare</small>',
  }

  childName = 'Lead figlia'
  childType: LeadType = 'elettrica-figlia'
  childRequester: number | null = null
  childOwners: number[] = []
  childCategories: number[] = []
  childTags: number[] = []
  installerAssignees: AssignmentAssignee[] = []

  childTypes: { text: string; value: LeadType }[] = [
    { text: 'Lead figlia generica (SA)', value: 'installatore-figlia' },
    { text: 'Lead figlia elettrica', value: 'elettrica-figlia' },
    { text: 'Lead figlia termica', value: 'termica-figlia' },
  ]

  @Watch('value')
  onValueChange(val: boolean) {
    if (val === true) {
      this.newAttachments = [];
      this.removedAttachments = [];

      if (this.dz) {
        this.dzOptions.removeType = 'client';
        this.dz.removeAllFiles();
        this.dzOptions.removeType = 'server';
      }

      this.childName = 'Lead figlia';
      this.childType = 'elettrica-figlia';
      this.childRequester = this.user.id;
      this.childOwners = [];
      this.childCategories = [];
      this.childTags = [];
      this.seedInstaller();
    }
    this.dialog = val;
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  @Watch('childType')
  onChildTypeChange() {
    if (this.showInstaller) {
      if (this.installerAssignees.length === 0) this.seedInstaller();
    } else {
      this.installerAssignees = [];
    }
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get userPermissions(): PermissionCode[] {
    return this.$store.getters.currentUserPermissions;
  }

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get users() {
    return this.$store.getters.users;
  }

  get categories(): Category[] {
    return this.$store.getters.categories;
  }

  get tags(): Tag[] {
    return this.$store.getters.tags;
  }

  get visibleCategories() {
    return Array.isArray(this.categories) ? this.categories.filter((c) => !c.hidden) : [];
  }

  get visibleTags() {
    return Array.isArray(this.tags) ? this.tags.filter((t) => !t.hidden) : [];
  }

  get installerUserGroupIds(): number[] {
    const cfg = this.$store.getters.getAssignmentType('installer');
    return cfg && Array.isArray(cfg.userGroupIds) ? cfg.userGroupIds : [1];
  }

  get installerUsers(): User[] {
    return Array.isArray(this.users)
      ? this.users.filter((u: User) => Array.isArray(u.groups)
        && u.groups.map((g) => g.id).some((gid) => this.installerUserGroupIds.includes(gid)))
      : [];
  }

  get showInstaller(): boolean {
    return ['elettrica-figlia', 'termica-figlia'].includes(this.childType)
      && this.hasPermission('installerAssignment.edit');
  }

  seedInstaller() {
    this.installerAssignees = this.showInstaller
      ? [{
        assigneeId: null, assigneeName: null, status: 'todo', assignedDate: null,
      }]
      : [];
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;
    return this.userPermissions.includes(codename);
  }

  leadCategory(categoryId: number) {
    if (!this.categories) return null;
    return this.categories.find((c: Category) => c.id === categoryId);
  }

  leadTag(tagId: number) {
    if (!this.tags) return null;
    return this.tags.find((tag) => tag.id === tagId);
  }

  tagColor(tagId: number): string {
    const tag = this.leadTag(tagId);
    const baseColor = tag?.color ? tag.color : 'light-blue';
    if (tag?.hidden || tag?.isActive === false) return `${baseColor} lighten-3`;
    return baseColor;
  }

  removeOwner(owner: User) {
    this.childOwners = this.childOwners.filter((id) => id !== owner.id);
  }

  removeCategory(category: Category) {
    this.childCategories = this.childCategories.filter((id) => id !== category.id);
  }

  removeTag(tag: Tag) {
    this.childTags = this.childTags.filter((id) => id !== tag.id);
  }

  dzMounted() {
    this.dz = 'attachmentsDropzone' in this.$refs ? this.$refs.attachmentsDropzone : null;
  }

  success(file: any, response: any) {
    if (response) this.newAttachments.push(response.id);
  }

  removedFile(file: any) {
    let serverId: number;
    if (file.xhr) {
      serverId = JSON.parse(file.xhr.response).id;
    } else {
      serverId = file.serverId;
    }
    this.removedAttachments.push(serverId);
    const aIndex = this.newAttachments.findIndex((a) => a === serverId);
    if (aIndex > -1) this.newAttachments.splice(aIndex, 1);
  }

  async removeAttachments() {
    if (this.removedAttachments.length > 0) {
      await API.removeLeadAttachments(this.lead.id, { attachments: this.removedAttachments });
    }
  }

  async onCancel() {
    this.dialog = false;
    setTimeout(() => { this.loading = false; }, 500);
  }

  async onCreate() {
    if (!this.childRequester) return;

    this.loading = true;

    try {
      await this.removeAttachments();

      const installer = this.showInstaller
        ? this.installerAssignees.filter((a) => a.assigneeId != null)
        : [];

      await this.$store.dispatch('createChild', {
        type: this.childType,
        name: this.childName || 'Lead figlia',
        requester: { id: this.childRequester },
        owners: this.childOwners,
        categories: this.childCategories,
        tags: this.childTags,
        attachments: this.newAttachments,
        installerAssignees: installer,
      });

      this.dialog = false;
    } finally {
      setTimeout(() => { this.loading = false; }, 500);
    }
  }
}
